import { useEffect, useRef, useState } from "react";
import { getAiRules, saveAiRules, startAiRulesTest, getAiRulesTest, previewAiRecheck, runAiRecheck, checkAiRules } from "../../../api/challenge";

const RED = '#b91c1c';
const GREEN = '#25a55f';
const errText = (e, fallback) => e?.response?.data?.error || fallback;
const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' IST' : '';
const SUGGESTION = { approve: 'Approve', approve_partly: 'Partly approve', needs_review: 'Needs your check', reject: 'Reject' };
const label = (o) => o ? `${SUGGESTION[o.suggestion] || o.suggestion} (${o.confidence}%)` : '-';
const Button = ({ onClick, disabled, children, color = GREEN, outline = false }) => (
    <button type="button" onClick={onClick} disabled={disabled}
        className="px-3 py-1.5 rounded font-semibold text-sm disabled:opacity-40"
        style={outline ? { border: `1px solid ${color}`, color } : { backgroundColor: color, color: '#fff' }}>{children}</button>
);

// The AI's judging rules, editable by the admin: general rules for all challenges + extra rules per challenge.
// "Test" runs the AI with the draft on a few past submissions (nothing is saved on them) so the effect is visible
// before saving. A saved change applies to the next submissions the AI reviews.
const AiRulesEditor = ({ challenges = [] }) => {
    const [open, setOpen] = useState(false);
    const [scope, setScope] = useState('general'); // 'general' or a challenge id
    const [data, setData] = useState(null);
    const [text, setText] = useState('');
    const [error, setError] = useState('');
    const [note, setNote] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testChallenge, setTestChallenge] = useState('');
    const [testCount, setTestCount] = useState(5);
    const [test, setTest] = useState(null);
    const [recheck, setRecheck] = useState(null);
    const [check, setCheck] = useState(null); // result of the rule check for the current text
    const timer = useRef(null);

    const isGeneral = scope === 'general';
    const current = data ? (isGeneral ? data.rules.general : data.rules.challenge) : null;
    const limit = data ? (isGeneral ? data.rules.limits.general : data.rules.limits.challenge) : 0;
    const changed = current && text !== (current.text || '');
    const challengeName = (id) => (challenges.find((c) => c._id === id) || {}).name || '';

    const load = async (sc) => {
        setError(''); setNote(null); setTest(null); setRecheck(null); setCheck(null);
        try {
            const d = await getAiRules(sc === 'general' ? '' : sc);
            setData(d);
            setText((sc === 'general' ? d.rules.general.text : d.rules.challenge.text) || '');
            if (sc !== 'general') setTestChallenge(sc);
        } catch (e) { setError(errText(e, 'Could not load the AI rules.')); }
    };

    useEffect(() => { if (open) load(scope); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, scope]);
    useEffect(() => () => clearTimeout(timer.current), []);

    // Every save is checked first: can the AI follow each rule with what it sees? Problems are shown before saving.
    const runCheck = async () => {
        const c = await checkAiRules(isGeneral ? { scope: 'general', text } : { scope: 'challenge', challengeId: scope, text, general: data.rules.general.text });
        setCheck({ ...c, forText: text });
        return c;
    };

    const checkOnly = async () => {
        setBusy(true); setNote(null);
        try { await runCheck(); } catch (e) { setNote({ ok: false, text: errText(e, 'Could not check the rules.') }); }
        finally { setBusy(false); }
    };

    const save = async (force = false) => {
        setBusy(true); setNote(null);
        if (!force) {
            try {
                const c = check && check.forText === text ? check : await runCheck();
                if (c.overall !== 'ok') { setBusy(false); setNote({ ok: false, text: 'Not saved yet: please look at the rule check below.' }); return; }
            } catch (e) { setBusy(false); setNote({ ok: false, text: errText(e, 'Could not check the rules. Please try again.') }); return; }
        }
        try {
            const r = await saveAiRules(isGeneral ? { scope: 'general', text } : { scope: 'challenge', challengeId: scope, text });
            setData({ ...data, rules: r });
            setText((isGeneral ? r.general.text : r.challenge.text) || '');
            setNote({ ok: true, text: force ? 'Saved (with the warnings above). The AI uses these rules for the next submissions it reviews.' : 'Checked and saved. The AI uses these rules for the next submissions it reviews.' });
        } catch (e) { setNote({ ok: false, text: errText(e, 'Could not save the rules.') }); }
        finally { setBusy(false); }
    };

    const poll = async (id) => {
        try {
            const t = await getAiRulesTest(id);
            setTest(t);
            if (t.status === 'running') timer.current = setTimeout(() => poll(id), 4000);
        } catch (e) { setError(errText(e, 'Could not read the test result.')); }
    };

    const runTest = async () => {
        const target = isGeneral ? testChallenge : scope;
        if (!target) { setError('Please pick a challenge to test on.'); return; }
        if (!window.confirm(`Test these rules on up to ${testCount} past submission(s) of "${challengeName(target)}"? Nothing is changed on them. AI cost: about Rs ${testCount * 3}.`)) return;
        setBusy(true); setError(''); setTest(null);
        try {
            const body = { challengeId: target, count: testCount };
            if (isGeneral) {
                // testing the general rules: keep that challenge's saved extra rules as they are
                const d = await getAiRules(target);
                body.general = text;
                body.challenge = d.rules.challenge.text || '';
            } else {
                body.general = data.rules.general.text;
                body.challenge = text;
            }
            const t = await startAiRulesTest(body);
            setTest(t);
            timer.current = setTimeout(() => poll(t._id), 3000);
        } catch (e) { setError(errText(e, 'Could not start the test.')); }
        finally { setBusy(false); }
    };

    const checkRecheck = async () => {
        setError('');
        try { setRecheck(await previewAiRecheck(isGeneral ? '' : scope)); }
        catch (e) { setError(errText(e, 'Could not count the pending submissions.')); }
    };

    const doRecheck = async () => {
        if (!window.confirm(`Send ${recheck.count} pending submission(s) back to the AI to be checked again with the saved rules? AI cost: about Rs ${recheck.estimatedCostRs}. Your automatic approval / rejection settings will apply to the results.`)) return;
        setBusy(true);
        try {
            const r = await runAiRecheck(isGeneral ? '' : scope);
            setRecheck({ ...recheck, queued: r.queued });
        } catch (e) { setError(errText(e, 'Could not start the re-check.')); }
        finally { setBusy(false); }
    };

    return (
        <div className="mb-4 px-4 py-3 rounded-md border border-[#e0a800] bg-[#fff8e1] text-gray-900">
            <button type="button" className="font-bold" onClick={() => setOpen(!open)}>{open ? '▾' : '▸'} AI review rules (what the AI checks)</button>
            {!open && <p className="text-sm mt-1">Change the rules the AI follows, for all challenges or for one challenge, and test them before saving.</p>}
            {open && (
                <div className="mt-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <label htmlFor="ai-rules-scope" className="font-semibold">Rules for:</label>
                        <select id="ai-rules-scope" value={scope} onChange={(e) => setScope(e.target.value)} className="px-2 py-1 border border-gray-300 rounded bg-white max-w-full">
                            <option value="general">All challenges (general rules)</option>
                            {challenges.map((c) => <option key={c._id} value={c._id}>{c.name}{data?.withChallengeRules?.some((r) => r.challengeId === c._id) ? ' (has extra rules)' : ''}</option>)}
                        </select>
                    </div>
                    <p className="mt-2">
                        {isGeneral
                            ? 'These rules apply to every challenge. Write each rule on its own line, starting with "-", in plain English.'
                            : 'Extra rules only for this challenge. They are added to the general rules and take priority over them. Leave empty if none.'}
                        {' '}The AI follows these rules; it cannot change the points calculation or how the review is recorded.
                    </p>
                    {error && <p className="mt-2" style={{ color: RED }}>{error}</p>}
                    {!data && !error && <p className="mt-2">Loading…</p>}
                    {data && current && (
                        <>
                            <textarea value={text} onChange={(e) => { setText(e.target.value); setNote(null); }} rows={isGeneral ? 14 : 6}
                                aria-label="AI rules" placeholder={isGeneral ? '' : '- e.g. A plastic tray or insert from a sweet box counts as a box.'}
                                className="mt-2 w-full p-2 border border-gray-300 rounded bg-white font-mono text-xs" />
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className="text-xs" style={{ color: text.length > limit ? RED : '#4b5563' }}>{text.length} / {limit} characters</span>
                                {current.updatedAt && <span className="text-xs text-gray-600">· Last changed {fmt(current.updatedAt)}{current.updatedBy?.email ? ` by ${current.updatedBy.email}` : ''}</span>}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                <Button onClick={() => save(false)} disabled={!changed || busy || text.length > limit}>{busy ? 'Checking…' : 'Check and save'}</Button>
                                <Button outline onClick={checkOnly} disabled={busy || !text.trim() || text.length > limit}>Check only</Button>
                                {changed && <Button outline color="#4b5563" onClick={() => setText(current.text || '')}>Undo my edits</Button>}
                                {isGeneral && text !== data.rules.defaultGeneral && <Button outline color="#4b5563" onClick={() => setText(data.rules.defaultGeneral)}>Load the original rules</Button>}
                                {note && <span className="font-semibold" style={{ color: note.ok ? '#1e7d48' : RED }}>{note.text}</span>}
                            </div>

                            {check && check.forText === text && (
                                <div className="mt-3 p-3 rounded bg-white border" data-testid="ai-rules-check" style={{ borderColor: check.overall === 'ok' ? GREEN : check.overall === 'problems' ? RED : '#e0a800' }}>
                                    <p className="font-semibold">
                                        {check.overall === 'ok' ? `Rule check: all ${check.counts.ok} rule(s) can be followed by the AI.`
                                            : `Rule check: ${check.counts.not_possible} rule(s) the AI cannot follow, ${check.counts.needs_change} to make clearer${check.counts.conflicts ? `, ${check.counts.conflicts} conflict(s)` : ''}.`}
                                    </p>
                                    {check.rules.filter((r) => r.status !== 'ok').map((r, i) => (
                                        <div key={i} className="mt-2">
                                            <p><span className="font-semibold" style={{ color: r.status === 'not_possible' ? RED : '#a16207' }}>{r.status === 'not_possible' ? 'Cannot work: ' : 'Make clearer: '}</span><span className="font-mono text-xs">{r.rule}</span></p>
                                            <p className="ml-3">{r.explanation}</p>
                                            {r.suggestion && <p className="ml-3 text-gray-700"><b>Suggestion:</b> {r.suggestion}</p>}
                                        </div>
                                    ))}
                                    {check.conflicts.map((k, i) => (
                                        <p key={`c${i}`} className="mt-2"><span className="font-semibold" style={{ color: '#a16207' }}>Rules that contradict each other: </span>{k.explanation}</p>
                                    ))}
                                    {check.overall !== 'ok' && changed && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            <Button outline color="#4b5563" onClick={() => setCheck(null)}>Edit the rules</Button>
                                            <Button color="#a16207" onClick={() => save(true)} disabled={busy}>Save anyway</Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {data.capabilities && (
                                <details className="mt-3">
                                    <summary className="cursor-pointer font-semibold">What the AI can and cannot see (read this when writing rules)</summary>
                                    <pre className="mt-1 p-2 bg-white border border-gray-200 rounded text-xs whitespace-pre-wrap font-sans">{data.capabilities}</pre>
                                </details>
                            )}

                            <div className="mt-4 pt-3 border-t border-[#e0a800]">
                                <p className="font-semibold">Test before saving</p>
                                <p>Runs the AI with the rules above on a few past submissions and shows the result next to your decision. Nothing is changed on those submissions.</p>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    {isGeneral && (
                                        <select value={testChallenge} onChange={(e) => setTestChallenge(e.target.value)} aria-label="Test on challenge" className="px-2 py-1 border border-gray-300 rounded bg-white max-w-full">
                                            <option value="">Pick a challenge to test on…</option>
                                            {challenges.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                                        </select>
                                    )}
                                    <select value={testCount} onChange={(e) => setTestCount(Number(e.target.value))} aria-label="Number of submissions" className="px-2 py-1 border border-gray-300 rounded bg-white">
                                        {[3, 5, 10].map((n) => <option key={n} value={n}>{n} submissions (about Rs {n * 3})</option>)}
                                    </select>
                                    <Button onClick={runTest} disabled={busy || test?.status === 'running' || text.length > limit}>Test these rules</Button>
                                </div>
                                {test && (
                                    <div className="mt-2" data-testid="ai-rules-test">
                                        <p>{test.status === 'running' ? `Testing on "${test.challengeName}"… ${test.done} of ${test.total} done (about 20 seconds each)` : `Test on "${test.challengeName}": ${test.done} of ${test.total} done.`}</p>
                                        {test.results?.length > 0 && (
                                            <div className="overflow-x-auto mt-1">
                                                <table className="text-xs bg-white border border-gray-200">
                                                    <thead><tr className="bg-gray-100">
                                                        <th className="p-1 text-left">User</th><th className="p-1 text-left">Submitted</th><th className="p-1 text-left">Your decision</th>
                                                        <th className="p-1 text-left">AI before</th><th className="p-1 text-left">AI with these rules</th><th className="p-1 text-left">AI's reason</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {test.results.map((r) => (
                                                            <tr key={r.id} className="border-t border-gray-200 align-top" style={r.changed ? { backgroundColor: '#fef3c7' } : {}}>
                                                                <td className="p-1">{r.user}</td>
                                                                <td className="p-1 whitespace-nowrap">{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''}</td>
                                                                <td className="p-1">{r.decision}</td>
                                                                <td className="p-1 whitespace-nowrap">{label(r.before)}</td>
                                                                <td className="p-1 whitespace-nowrap font-semibold">{r.error ? <span style={{ color: RED }}>could not test</span> : label(r.after)}{r.changed ? ' (changed)' : ''}</td>
                                                                <td className="p-1 min-w-[16rem]">{r.after?.summary || r.error || ''}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 pt-3 border-t border-[#e0a800]">
                                <p className="font-semibold">Re-check pending submissions with the saved rules</p>
                                <p>Pending submissions were checked with the rules at that time. You can send them back to the AI to be checked again{isGeneral ? '' : ' (only this challenge)'}; your automatic approval / rejection settings then apply.</p>
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <Button outline onClick={checkRecheck} disabled={busy || changed}>How many?</Button>
                                    {changed && <span className="text-xs text-gray-600">Save your changes first.</span>}
                                    {recheck && recheck.queued === undefined && (
                                        <>
                                            <span>{recheck.count} pending submission(s), about Rs {recheck.estimatedCostRs}{recheck.count > recheck.max ? ` (the first ${recheck.max} will be sent)` : ''}.</span>
                                            <Button onClick={doRecheck} disabled={busy || recheck.count === 0}>Re-check {Math.min(recheck.count, recheck.max)}</Button>
                                        </>
                                    )}
                                    {recheck?.queued !== undefined && <span className="font-semibold" style={{ color: '#1e7d48' }}>{recheck.queued} sent to the AI. They will be checked again within the next few minutes.</span>}
                                </div>
                            </div>

                            {current.history?.length > 0 && (
                                <details className="mt-4 pt-3 border-t border-[#e0a800]">
                                    <summary className="font-semibold cursor-pointer">Earlier versions ({current.history.length})</summary>
                                    <ul className="mt-1">
                                        {current.history.map((h, i) => (
                                            <li key={i} className="flex flex-wrap items-center gap-2 py-1">
                                                <span className="text-xs">Before the change on {fmt(h.at)}{h.by?.email ? ` by ${h.by.email}` : ''} ({(h.text || '').length} characters)</span>
                                                <Button outline color="#4b5563" onClick={() => { setText(h.text || ''); setNote({ ok: true, text: 'Earlier version loaded. Test it, or press Check and save to use it again.' }); }}>Load this version</Button>
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AiRulesEditor;
